import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Select,
    MenuItem,
    FormControl,
    Divider,
} from '@mui/material';
import {
    Close,
    Dashboard as DashboardIcon,
    Business as BusinessIcon,
    People as PeopleIcon,
    AttachMoney as AttachMoneyIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Warning as WarningIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { THEME_COLORS } from '../../constants';
import { formatCelular } from '../../utils';
import { useDistributors } from '../../hooks';

const DistributorDashboard = ({ open, onClose, distributor }) => {
    const { dashboardData, dashboardLoading, dashboardError, loadDistributorDashboard } = useDistributors();
    const [activeTab, setActiveTab] = useState(0);
    const [contactsPage, setContactsPage] = useState(1);
    const [contactsLimit, setContactsLimit] = useState(10);

    useEffect(() => {
        if (open && distributor) {
            loadDistributorDashboard(distributor.distributor_id, {
                contactsPage,
                contactsLimit
            });
        }
    }, [open, distributor, contactsPage, contactsLimit, loadDistributorDashboard]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleContactsPageChange = (newPage) => {
        setContactsPage(newPage);
    };

    const handleContactsLimitChange = (event) => {
        setContactsLimit(event.target.value);
        setContactsPage(1); // Reset to first page
    };

    const getSubscriptionStatusChip = (status, endDate) => {
        const now = new Date();
        const end = new Date(endDate);
        const daysUntilExpiry = Math.ceil((end - now) / (1000 * 60 * 60 * 24));

        switch (status) {
            case 'activa':
                if (daysUntilExpiry <= 3) {
                    return (
                        <Chip
                            icon={<WarningIcon />}
                            label="Vence Pronto"
                            size="small"
                            sx={{
                                backgroundColor: THEME_COLORS.TEXT_MUTED,
                                color: 'white',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                '& .MuiChip-icon': { color: 'white', fontSize: '14px' },
                            }}
                        />
                    );
                } else if (daysUntilExpiry <= 7) {
                    return (
                        <Chip
                            icon={<WarningIcon />}
                            label="Vence en 7 días"
                            size="small"
                            sx={{
                                backgroundColor: THEME_COLORS.SECONDARY,
                                color: 'white',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                '& .MuiChip-icon': { color: 'white', fontSize: '14px' },
                            }}
                        />
                    );
                }
                return (
                    <Chip
                        icon={<CheckCircleIcon />}
                        label="Activa"
                        size="small"
                        sx={{
                            backgroundColor: THEME_COLORS.TEXT_SECONDARY,
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: 'white', fontSize: '14px' },
                        }}
                    />
                );
            case 'vencida':
                return (
                    <Chip
                        icon={<CancelIcon />}
                        label="Vencida"
                        size="small"
                        sx={{
                            backgroundColor: THEME_COLORS.TEXT_MUTED,
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: 'white', fontSize: '14px' },
                        }}
                    />
                );
            case 'suspendida':
                return (
                    <Chip
                        icon={<CancelIcon />}
                        label="Suspendida"
                        size="small"
                        sx={{
                            backgroundColor: THEME_COLORS.TEXT_MUTED,
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: 'white', fontSize: '14px' },
                        }}
                    />
                );
            case 'cancelada':
                return (
                    <Chip
                        icon={<CancelIcon />}
                        label="Cancelada"
                        size="small"
                        sx={{
                            backgroundColor: THEME_COLORS.SECONDARY,
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: 'white', fontSize: '14px' },
                        }}
                    />
                );
            default:
                return (
                    <Chip
                        icon={<CancelIcon />}
                        label="Sin Suscripción"
                        size="small"
                        sx={{
                            backgroundColor: THEME_COLORS.TEXT_MUTED,
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: 'white', fontSize: '14px' },
                        }}
                    />
                );
        }
    };

    const StatCard = ({ title, value, icon, color, subtitle }) => (
        <Card
            sx={{
                height: '100%',
                border: `1px solid ${THEME_COLORS.BORDER}`,
                borderRadius: '12px',
                backgroundColor: THEME_COLORS.WHITE,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                    borderColor: color,
                },
            }}
        >
            <CardContent sx={{ padding: 2.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{
                                color: color,
                                fontWeight: 700,
                                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                                marginBottom: 0.5,
                            }}
                        >
                            {value}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: THEME_COLORS.TEXT_SECONDARY,
                                fontWeight: 500,
                                fontSize: '0.875rem',
                            }}
                        >
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: THEME_COLORS.TEXT_MUTED,
                                    fontSize: '0.75rem',
                                    marginTop: 0.5,
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 50,
                            height: 50,
                            borderRadius: '10px',
                            backgroundColor: `${color}15`,
                        }}
                    >
                        {React.cloneElement(icon, { sx: { fontSize: '1.5rem', color } })}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    const ContactsPagination = () => {
        if (!dashboardData?.contactsPagination) return null;

        const { page, pages, total, hasNext, hasPrev } = dashboardData.contactsPagination;

        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 2,
                    borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                    backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
                }}
            >
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                    Mostrando {((page - 1) * contactsLimit) + 1} - {Math.min(page * contactsLimit, total)} de {total} contactos
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                            value={contactsLimit}
                            onChange={handleContactsLimitChange}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                                },
                                '& .MuiSelect-select': {
                                    color: THEME_COLORS.TEXT_PRIMARY,
                                    fontSize: '0.875rem',
                                },
                            }}
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={!hasPrev}
                            onClick={() => handleContactsPageChange(page - 1)}
                            startIcon={<NavigateBeforeIcon />}
                            sx={{
                                color: THEME_COLORS.TEXT_SECONDARY,
                                borderColor: THEME_COLORS.BORDER_LIGHT,
                                borderRadius: '8px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: THEME_COLORS.TEXT_PRIMARY,
                                    color: THEME_COLORS.TEXT_PRIMARY,
                                },
                                '&:disabled': {
                                    color: THEME_COLORS.TEXT_MUTED,
                                    borderColor: THEME_COLORS.BORDER_LIGHT,
                                },
                            }}
                        >
                            Anterior
                        </Button>

                        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, px: 2 }}>
                            Página {page} de {pages || 1}
                        </Typography>

                        <Button
                            variant="outlined"
                            size="small"
                            disabled={!hasNext}
                            onClick={() => handleContactsPageChange(page + 1)}
                            endIcon={<NavigateNextIcon />}
                            sx={{
                                color: THEME_COLORS.TEXT_SECONDARY,
                                borderColor: THEME_COLORS.BORDER_LIGHT,
                                borderRadius: '8px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: THEME_COLORS.TEXT_PRIMARY,
                                    color: THEME_COLORS.TEXT_PRIMARY,
                                },
                                '&:disabled': {
                                    color: THEME_COLORS.TEXT_MUTED,
                                    borderColor: THEME_COLORS.BORDER_LIGHT,
                                },
                            }}
                        >
                            Siguiente
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    };

    if (!distributor) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                    backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                    boxShadow: `0 8px 32px ${THEME_COLORS.SHADOW_LIGHT}`,
                    overflow: 'hidden',
                    maxHeight: '90vh',
                }
            }}
        >
            <DialogTitle
                sx={{
                    padding: 3,
                    paddingBottom: 2,
                    backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
                    borderBottom: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '12px',
                                backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <DashboardIcon sx={{ color: THEME_COLORS.WHITE, fontSize: '1.5rem' }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '1.25rem' }}>
                                Dashboard - {distributor.nombre_empresa}
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.875rem' }}>
                                Estadísticas y contactos del distribuidor
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ padding: 0 }}>
                {dashboardLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                        <CircularProgress />
                    </Box>
                ) : dashboardError ? (
                    <Box sx={{ padding: 3 }}>
                        <Alert severity="error" sx={{ borderRadius: '12px' }}>
                            {dashboardError}
                        </Alert>
                    </Box>
                ) : dashboardData ? (
                    <Box>
                        {/* Estadísticas */}
                        <Box sx={{ padding: 3, paddingBottom: 2 }}>
                            <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY }}>
                                Estadísticas del Distribuidor
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard
                                        title="Total Contactos"
                                        value={dashboardData.stats.total_contacts}
                                        icon={<PeopleIcon />}
                                        color={THEME_COLORS.TEXT_PRIMARY}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard
                                        title="Suscripciones Activas"
                                        value={dashboardData.stats.active_subscriptions}
                                        icon={<CheckCircleIcon />}
                                        color={THEME_COLORS.TEXT_SECONDARY}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard
                                        title="Ingresos Totales"
                                        value={`$${dashboardData.stats.total_revenue.toLocaleString('es-CO')}`}
                                        icon={<AttachMoneyIcon />}
                                        color={THEME_COLORS.SECONDARY}
                                        subtitle={`Promedio: $${Math.round(dashboardData.stats.average_subscription_amount).toLocaleString('es-CO')}`}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard
                                        title="Vencen Pronto"
                                        value={dashboardData.stats.expiring_soon}
                                        icon={<WarningIcon />}
                                        color={THEME_COLORS.TEXT_MUTED}
                                        subtitle="Próximos 7 días"
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider />

                        {/* Tabs */}
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={activeTab} onChange={handleTabChange} sx={{ paddingX: 3 }}>
                                <Tab label="Contactos" />
                                <Tab label="Estadísticas Detalladas" />
                            </Tabs>
                        </Box>

                        {/* Tab Content */}
                        {activeTab === 0 && (
                            <Box>
                                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: THEME_COLORS.LIGHT_GRAY }}>
                                                <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Contacto</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Celular</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Email</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Suscripción</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Monto</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_SECONDARY }}>Vencimiento</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dashboardData.contacts.map((contact) => (
                                                <TableRow key={contact.contact_id}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {contact.nombre}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <PhoneIcon sx={{ fontSize: 14, color: THEME_COLORS.TEXT_SECONDARY }} />
                                                            <Typography variant="body2">
                                                                {formatCelular(contact.celular)}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <EmailIcon sx={{ fontSize: 14, color: THEME_COLORS.TEXT_SECONDARY }} />
                                                            <Typography variant="body2">
                                                                {contact.email_ewelink || 'No configurado'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {getSubscriptionStatusChip(contact.subscription_status, contact.fecha_fin)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {contact.monto ? `$${contact.monto.toLocaleString('es-CO')}` : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {contact.fecha_fin ? new Date(contact.fecha_fin).toLocaleDateString() : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <ContactsPagination />
                            </Box>
                        )}

                        {activeTab === 1 && (
                            <Box sx={{ padding: 3 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Card sx={{ border: `1px solid ${THEME_COLORS.BORDER}`, borderRadius: '12px' }}>
                                            <CardContent>
                                                <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600 }}>
                                                    Estado de Suscripciones
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">Activas:</Typography>
                                                        <Chip label={dashboardData.stats.active_subscriptions} size="small" sx={{ backgroundColor: THEME_COLORS.TEXT_SECONDARY, color: 'white' }} />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">Vencidas:</Typography>
                                                        <Chip label={dashboardData.stats.expired_subscriptions} size="small" sx={{ backgroundColor: THEME_COLORS.TEXT_MUTED, color: 'white' }} />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">Suspendidas:</Typography>
                                                        <Chip label={dashboardData.stats.suspended_subscriptions} size="small" sx={{ backgroundColor: THEME_COLORS.TEXT_MUTED, color: 'white' }} />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">Canceladas:</Typography>
                                                        <Chip label={dashboardData.stats.cancelled_subscriptions} size="small" sx={{ backgroundColor: THEME_COLORS.SECONDARY, color: 'white' }} />
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Card sx={{ border: `1px solid ${THEME_COLORS.BORDER}`, borderRadius: '12px' }}>
                                            <CardContent>
                                                <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600 }}>
                                                    Información del Distribuidor
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED }}>Empresa:</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{distributor.nombre_empresa}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED }}>Distribuidor:</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{distributor.nombre_distribuidor}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED }}>Celular:</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCelular(distributor.celular)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED }}>Cuenta eWeLink:</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{distributor.cuenta_ewelink}</Typography>
                                                    </Box>
                                                    {distributor.ciudad && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED }}>Ubicación:</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {distributor.ciudad}{distributor.estado ? `, ${distributor.estado}` : ''}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions
                sx={{
                    padding: 3,
                    paddingTop: 2,
                    backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
                    borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                }}
            >
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                        color: THEME_COLORS.WHITE,
                        borderRadius: '12px',
                        paddingX: 3,
                        paddingY: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: THEME_COLORS.TEXT_PRIMARY },
                    }}
                >
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DistributorDashboard;